import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation
} from '@angular/core';
import type { Connection1cFindInterface } from '@zaoblako/shared/modules/integration/dtos/connection-1c/connection-1c-find.dto';
import { BankEnum } from '@zaoblako/web/core/models/enums/bank.enum';
import { BasisSystem1cService } from '@zaoblako/web/core/services/basis/basis-system-1c.service';
import { DocumentRegistryService } from '@zaoblako/web/core/services/document/document-registry.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { Integration1cService } from '@zaoblako/web/core/services/integration/integration1c.service';
import { FileUploaderComponent } from '@zaoblako/web/shared/components/file-uploader/file-uploader.component';
import { TypeFormEnum } from '@zaoblako/web/shared/components/form-sidebar/enums/type.form.enum';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ImageComponent } from '@zaoblako/web/shared/dumbs/image/image.component';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { Subscription } from 'rxjs';
import { WelcomeSystemStepEnum } from '../../enums/welcome-system-step.enum';

type ImageType = {
  src: string;
  alt: string;
  isShowForm1C?: boolean;
  isShowLoadFile?: boolean;
  isShowNext?: boolean;
  isShowExtension?: boolean;
};

@Component({
  standalone: true,
  selector: 'crm-welcome-system-info-import',
  templateUrl: './welcome-system-info-import.component.html',
  styleUrls: ['./welcome-system-info-import.component.scss'],
  imports: [CommonModule, FileUploaderComponent, ButtonComponent, ImageComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class WelcomeSystemInfoImportComponent implements OnInit, OnDestroy {
  @Input() public step: WelcomeSystemStepEnum;
  @Input() public bank: BankEnum;

  @Output() public closeModal = new EventEmitter<void>();

  public loading = false;
  public loadingDownload = false;

  public databases: Connection1cFindInterface[] = [];

  public imagesBank: Record<BankEnum, ImageType[]> = {
    [BankEnum.SBERBANK]: [
      {
        src: 'assets/images/welcome-system/sber/C1.png',
        alt: 'C1'
      },
      {
        src: 'assets/images/welcome-system/sber/C2.png',
        alt: 'C2'
      },
      {
        src: 'assets/images/welcome-system/sber/C3.png',
        alt: 'C3'
      },
      {
        src: 'assets/images/welcome-system/sber/C4.png',
        alt: 'C4',
        isShowLoadFile: true
      }
    ],
    [BankEnum.TINKOFF]: [
      {
        src: 'assets/images/welcome-system/tinkoff/T1.png',
        alt: 'T1'
      },
      {
        src: 'assets/images/welcome-system/tinkoff/T2.png',
        alt: 'T2'
      },
      {
        src: 'assets/images/welcome-system/tinkoff/T3.png',
        alt: 'T3'
      },
      {
        src: 'assets/images/welcome-system/tinkoff/T4.png',
        alt: 'T4',
        isShowLoadFile: true
      }
    ],
    [BankEnum.ALFA]: [
      {
        src: 'assets/images/welcome-system/alfa/A1.png',
        alt: 'A1'
      },
      {
        src: 'assets/images/welcome-system/alfa/A2.png',
        alt: 'A2'
      },
      {
        src: 'assets/images/welcome-system/alfa/A3.png',
        alt: 'A3'
      },
      {
        src: 'assets/images/welcome-system/alfa/A4.png',
        alt: 'A4'
      },
      {
        src: 'assets/images/welcome-system/alfa/A5.png',
        alt: 'A5'
      },
      {
        src: 'assets/images/welcome-system/alfa/A6.png',
        alt: 'A6',
        isShowLoadFile: true
      }
    ]
  };

  public images1C: ImageType[] = [
    {
      src: 'assets/images/welcome-system/1c/1.png',
      alt: '1'
    },
    {
      src: 'assets/images/welcome-system/1c/2.png',
      alt: '2'
    },
    {
      src: 'assets/images/welcome-system/1c/3.png',
      alt: '3',
      isShowExtension: true
    },
    {
      src: 'assets/images/welcome-system/1c/4.png',
      alt: '4'
    },
    {
      src: 'assets/images/welcome-system/1c/5.png',
      alt: '5'
    },
    {
      src: 'assets/images/welcome-system/1c/6.png',
      alt: '6'
    },
    {
      src: 'assets/images/welcome-system/1c/7.png',
      alt: '7'
    },
    {
      src: 'assets/images/welcome-system/1c/8.png',
      alt: '8'
    },
    {
      src: 'assets/images/welcome-system/1c/9.png',
      alt: '9'
    },
    {
      src: 'assets/images/welcome-system/1c/10.png',
      alt: '10'
    },
    {
      src: 'assets/images/welcome-system/1c/11.png',
      alt: '11'
    },
    {
      src: 'assets/images/welcome-system/1c/12.png',
      alt: '12'
    },
    {
      src: 'assets/images/welcome-system/1c/13.png',
      alt: '13'
    },
    {
      src: 'assets/images/welcome-system/1c/14.png',
      alt: '14',
      isShowForm1C: true
    },
    {
      src: 'assets/images/welcome-system/1c/15.png',
      alt: '15'
    },
    {
      src: 'assets/images/welcome-system/1c/16.png',
      alt: '16'
    },
    {
      src: 'assets/images/welcome-system/1c/17.png',
      alt: '17'
    },
    {
      src: 'assets/images/welcome-system/1c/18.png',
      alt: '18'
    },
    {
      src: 'assets/images/welcome-system/1c/19.png',
      alt: '19',
      isShowNext: true
    }
  ];

  public activeImageIndex = 0;
  public activeImage: ImageType;

  private formSubscription$: Subscription = new Subscription();

  public get isShowNextButton(): boolean {
    if (this.step === WelcomeSystemStepEnum.IMPORT_FROM_BANK) {
      return !!this.imagesBank[this.bank][this.activeImageIndex + 1];
    } else {
      return !!this.images1C[this.activeImageIndex + 1];
    }
  }

  public get isShowBackButton(): boolean {
    return this.activeImageIndex !== 0;
  }

  public get WelcomeSystemStepEnum() {
    return WelcomeSystemStepEnum;
  }

  public constructor(
    private readonly documentRegistryService: DocumentRegistryService,
    private readonly integration1cService: Integration1cService,
    private readonly basisSystem1cService: BasisSystem1cService,
    private readonly formStoreService: FormStoreService,
    private readonly errorService: ErrorService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit() {
    if (this.step === WelcomeSystemStepEnum.IMPORT_FROM_BANK) {
      this.activeImage = this.imagesBank[this.bank][0];
    } else {
      this.activeImage = this.images1C[0];
    }

    this.integration1cService.find<Connection1cFindInterface>().subscribe({
      next: (databases) => {
        this.databases = databases;
        this.cdRef.markForCheck();
      }
    });
  }

  public ngOnDestroy() {
    this.formSubscription$?.unsubscribe();
  }

  public nextImage() {
    if (this.step === WelcomeSystemStepEnum.IMPORT_FROM_BANK) {
      this.activeImageIndex = this.imagesBank[this.bank][this.activeImageIndex + 1]
        ? this.activeImageIndex + 1
        : this.activeImageIndex;
      this.activeImage = this.imagesBank[this.bank][this.activeImageIndex];
    } else {
      if (this.activeImage.isShowForm1C && !this.databases?.length) {
        return this.errorService.addCustomError('Заполните сперва реквизиты 1С');
      }

      this.activeImageIndex = this.images1C[this.activeImageIndex + 1]
        ? this.activeImageIndex + 1
        : this.activeImageIndex;
      this.activeImage = this.images1C[this.activeImageIndex];
    }
  }

  public backImage() {
    if (this.step === WelcomeSystemStepEnum.IMPORT_FROM_BANK) {
      this.activeImageIndex = this.activeImageIndex !== 0 ? this.activeImageIndex - 1 : this.activeImageIndex;
      this.activeImage = this.imagesBank[this.bank][this.activeImageIndex];
    } else {
      this.activeImageIndex = this.activeImageIndex !== 0 ? this.activeImageIndex - 1 : this.activeImageIndex;
      this.activeImage = this.images1C[this.activeImageIndex];
    }
  }

  public importFromBankStatement(files: File[]) {
    this.loading = true;
    this.cdRef.markForCheck();

    const formData = new FormData();
    formData.append('file', files[0]);

    this.documentRegistryService.fromBankStatement(formData).subscribe({
      next: () => {
        this.errorService.addSuccessMessage('Выписка успешно загружена');
        this.loading = false;
        this.cdRef.markForCheck();
        this.closeModal.emit();
      },
      error: () => {
        this.loading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  public downloadExtension() {
    this.loadingDownload = true;
    this.cdRef.markForCheck();

    this.basisSystem1cService.download().subscribe((data) => {
      const file = new Blob([data], { type: 'application/octet-stream' });
      let url = URL.createObjectURL(file);
      let a = document.createElement('a');
      document.body.appendChild(a);
      a.setAttribute('style', 'display: none');
      a.href = url;
      a.download = 'ЗаФинансы.cfe';
      a.click();
      URL.revokeObjectURL(url);
      a.remove();
      this.loadingDownload = false;
      this.cdRef.markForCheck();
    });
  }

  public showAddDatabase() {
    const result = this.formStoreService.addForm<Connection1cFindInterface>(TypeFormEnum.DATABASE_1C, {
      headerText: 'Создание базы 1С',
      widthSize: 'form-sidebar-small',
      typeZIndex: 'z-index-2000',
      isNotShowRedirectInCreate: true,
      data: {
        isWelcomeSystem: true
      }
    });

    this.formSubscription$.add(
      result.onSave.subscribe((database) => {
        this.databases.push(database);
        this.databases = [...this.databases];
        this.cdRef.markForCheck();
      })
    );
  }
}
